/**
 * TanStack Query configuration.
 *
 * All server state lives here. No `useEffect` + `fetch`, no `setInterval` polling —
 * both are ESLint errors. This is what makes request cancellation, dedupe and bounded
 * polling structural rather than something each screen has to remember.
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api/errors';

/** Retrying a 4xx just delays showing the user the truth. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.isNetwork) return failureCount < 2;
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        // The legacy app re-fetched full tables on every window focus.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        // A mutation that spends provider credits must never retry on its own.
        retry: false,
      },
    },
  });
}
